import {
  ClockCountdown,
  GlobeHemisphereWest,
  Info,
} from "@phosphor-icons/react";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

import IconAvatar from "common/components/IconAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { typedObjectEntries } from "common/helpers/utils";
import type { ScheduleInterval, Schedule as ScheduleType } from "common/types";

import TimezoneSelect from "../TimezoneSelect";
import ScheduleItem from "./ScheduleItem";

import type { Dispatch, SetStateAction } from "react";
import type { TimezoneOption } from "../../types";

interface ScheduleProps {
  schedule: ScheduleType;
  timezone: TimezoneOption;
  setSchedule: Dispatch<SetStateAction<ScheduleType>>;
  setSelectedTimezone: Dispatch<SetStateAction<TimezoneOption>>;
  isDisabled?: boolean;
}

export default function Schedule({
  schedule,
  timezone,
  setSchedule,
  setSelectedTimezone,
  isDisabled = false,
}: ScheduleProps) {
  const { t } = useTranslation("campaigns");

  function setRanges(day: keyof ScheduleType, ranges: ScheduleInterval[]) {
    setSchedule((prevSchedule) => {
      const newSchedule = produce(prevSchedule, (draftState) => {
        if (!ranges.length) {
          draftState[day].intervals = [];
        } else {
          draftState[day].intervals = ranges;
        }
      });
      if (Object.values(newSchedule).some(({ intervals }) => !!intervals)) {
        return newSchedule;
      }
      return prevSchedule;
    });
  }

  function applyToActiveDays(ranges: ScheduleInterval[]) {
    typedObjectEntries(schedule)
      .filter(([_, { intervals }]) => intervals.length)
      .forEach(([key]) => {
        setRanges(key, ranges);
      });
  }

  return (
    <article className="rounded-3xl border border-black-200 p-3">
      {/* Timezone */}
      <div className="mb-4 flex flex-col gap-3 border-b border-black-200 pb-4 lg:flex-row lg:items-center">
        <div className="flex w-full gap-2.5 lg:items-center">
          <IconAvatar>
            <GlobeHemisphereWest />
          </IconAvatar>

          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-1">
              <h3 className="text-md-bold"> {t("timezoneTitle")}</h3>
              <Tooltip>
                <TooltipTrigger>
                  <Info weight="fill" size={20} className="fill-black-300" />
                </TooltipTrigger>
                <TooltipContent>{t("timezoneTooltip")}</TooltipContent>
              </Tooltip>
            </div>

            <p className="text-xs-medium text-black-500">
              {t("timezoneDescription")}
            </p>
          </div>
        </div>

        <TimezoneSelect
          selectedTimezone={timezone}
          onSelect={(newTimezone) => setSelectedTimezone(newTimezone)}
          isDisabled={isDisabled}
          isScheduling={false}
          className="w-full lg:w-80"
        />
      </div>

      {/* Working hours */}
      <div className="flex items-center gap-x-2.5 max-lg:mb-4">
        <IconAvatar>
          <ClockCountdown />
        </IconAvatar>
        <div className="flex flex-col gap-y-1">
          <h3 className="text-md-bold"> {t("workingHoursTitle")}</h3>
          <p className="text-xs-medium text-black-500">
            {t("workingHoursDescription")}
          </p>
        </div>
      </div>

      {typedObjectEntries(schedule).map(([day, { intervals }]) => (
        <ScheduleItem
          isDisabled={isDisabled}
          key={day}
          day={day}
          ranges={intervals}
          setRanges={(ranges: ScheduleInterval[]) => {
            setRanges(day, ranges);
          }}
          onApplyToAll={applyToActiveDays}
        />
      ))}
    </article>
  );
}
